/**
 * Escape handling for gl-modal, shared by every modal on screen.
 *
 * Modals nest across components (a confirm inside an info modal, for example),
 * so a per-component listener would close the whole pile at once. One document
 * listener plus a LIFO stack means Escape only ever reaches the topmost modal.
 */

// Open modals, oldest first; the last entry owns Escape.
const stack = []

// Widgets that render their own dismissible layer. Each class is present only
// while that layer is open, and each library closes on Escape without calling
// preventDefault — so without this check Escape would shut the modal behind an
// open select instead of the select itself.
const OVERLAY_SELECTORS = [
  '.vs--open', // vue-select
  '.vue-treeselect--open', // @riophae/vue-treeselect
  '.mx-datepicker-popup', // vue2-datepicker
  '.datetimepicker.visible', // vue-ctk-date-time-picker
  '.popover.open', // v-tooltip
  '.mc-overlay', // gl-mission-control, which closes on Escape itself
]

const isEscape = event => event.key === 'Escape' || event.key === 'Esc'

const isOverlayOpen = () => OVERLAY_SELECTORS.some(selector => document.querySelector(selector))

// Whether one of those layers was open at the moment the key went down.
let overlayOwnedThisPress = false

// Read in the capture phase, before any widget can react. Reading it later, in
// the bubble phase, only works while the widget happens to close itself
// asynchronously — one that drops its own class synchronously would be gone by
// then and the dialog behind it would close along with it.
const onKeydownCapture = event => {
  if (!isEscape(event)) return

  overlayOwnedThisPress = isOverlayOpen()
}

const onKeydown = event => {
  if (!isEscape(event)) return

  const ownedByOverlay = overlayOwnedThisPress
  overlayOwnedThisPress = false

  if (event.defaultPrevented) return
  // A held key must not walk down the stack closing one dialog per repeat.
  if (event.repeat) return
  if (ownedByOverlay) return

  const top = stack[stack.length - 1]
  if (!top) return

  event.preventDefault()
  event.stopPropagation()
  top()
}

// The decision is taken in the bubble phase, so a widget gets the key first;
// only the snapshot above is taken in capture.
const attach = () => {
  document.addEventListener('keydown', onKeydownCapture, true)
  document.addEventListener('keydown', onKeydown)
}
const detach = () => {
  document.removeEventListener('keydown', onKeydownCapture, true)
  document.removeEventListener('keydown', onKeydown)
}

export const pushEscHandler = handler => {
  if (stack.includes(handler)) return
  if (!stack.length) attach()
  stack.push(handler)
}

export const removeEscHandler = handler => {
  const at = stack.indexOf(handler)
  if (at === -1) return

  stack.splice(at, 1)
  if (!stack.length) detach()
}

// Tests open and close modals across cases; without this the stack leaks.
export const __resetEscStack = () => {
  stack.length = 0
  overlayOwnedThisPress = false
  detach()
}
